import { apiFetch } from './apiFetch';
import { getAwsCreds, AwsCreds } from './awsCredentials';

/**
 * AWS account IDs used by the FM Bundle feature. Credentials are resolved by account ID
 * (via resolveAccountCreds) rather than by a hardcoded alias, so the feature keeps working
 * even if the account's Alias in Settings changes.
 * - Catalog sync talks to the Application account (649150965830).
 * - FM provisioning / status checks use the Image Builder account (428127313411).
 */
export const FM_CATALOG_SYNC_ACCOUNT_ID = '649150965830';
export const FM_PROVISIONING_ACCOUNT_ID = '428127313411';

export type FmBundleStatus = 'provisioning' | 'active' | 'failed' | 'deleting' | 'deleted';

export interface FmEnvironment {
  documentId?: string;
  name: string;
  isProduction: boolean;
}

export interface FmProfile {
  shortName: string;
  modelId: string;
  provider?: string;
  inferenceProfileId?: string;
  aipArn?: string;
}

export interface FmProvisioningRequest {
  action: string;
  status: string;
  executionId?: string;
  requestedBy?: string;
  submittedAt?: string;
  completedAt?: string | null;
  statusMessage?: string | null;
}

export interface FmBundle {
  documentId?: string;
  id?: number;
  projectSlug: string;
  serviceName?: string;
  team: string;
  roleName?: string;
  roleArn?: string;
  policyArn?: string;
  monthlyBudgetUSD?: number | null;
  yearlyBudgetUSD?: number | null;
  businessEmails?: string[] | null;
  computeRoles?: string[] | null;
  currentStatus: FmBundleStatus;
  lastSyncedAt?: string | null;
  environment?: FmEnvironment | null;
  fm_profiles?: FmProfile[];
  fm_provisioning_requests?: FmProvisioningRequest[];
}

export interface FmBundleStatusResult {
  currentStatus: FmBundleStatus;
  ssmStatus?: string;
  inProgress: boolean;
  dryRun?: boolean;
  request?: FmProvisioningRequest | null;
  steps?: Array<{ name?: string; status?: string; message?: string }>;
}

export interface FmCatalogModel {
  modelId: string;
  modelName: string;
  supportsInferenceProfile: boolean;
  lifecycleStatus: string;
}

export interface FmCatalogProvider {
  provider: string;
  models: FmCatalogModel[];
}

export interface FmCatalog {
  providers: FmCatalogProvider[];
  total: number;
}

export interface FmModelSyncResult {
  synced: number;
  created: number;
  updated: number;
  region: string;
}

/** Raised when the account creds required for a call are missing from Settings. */
export class MissingCredsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingCredsError';
  }
}

/** Caches AWS account ID -> alias so we only look an account up once per page load. */
const accountAliasCache = new Map<string, string>();

/**
 * Resolves the Settings alias and stored AWS creds for a given AWS account ID.
 * Throws MissingCredsError (with a user-facing message) when the account is not registered
 * in ITPMNG or when its credentials are not stored in Settings.
 */
async function resolveAccountCreds(accountId: string): Promise<{ alias: string; creds: AwsCreds }> {
  let alias = accountAliasCache.get(accountId);
  if (!alias) {
    const res = await apiFetch(
      `/accounts?filters[accountid][$eq]=${encodeURIComponent(accountId)}&fields[0]=Alias&fields[1]=accountid`,
    );
    const json = await parseJson<{ data: Array<{ Alias: string }> }>(res);
    alias = json.data?.[0]?.Alias;
    if (!alias) {
      throw new MissingCredsError(
        `No account with ID ${accountId} is registered in ITPMNG. Add it under Integrations > Accounts.`,
      );
    }
    accountAliasCache.set(accountId, alias);
  }
  const creds = getAwsCreds(alias);
  if (!creds) {
    throw new MissingCredsError(
      `AWS credentials for account "${alias}" (${accountId}) are not stored. Add them in Settings.`,
    );
  }
  return { alias, creds };
}

/**
 * Returns true when FM provisioning creds (Image Builder account) are available. Used by the
 * wizard to warn before submit without throwing. Swallows resolution errors and returns false.
 */
export async function hasFmProvisioningCreds(): Promise<boolean> {
  try {
    await resolveAccountCreds(FM_PROVISIONING_ACCOUNT_ID);
    return true;
  } catch {
    return false;
  }
}

function awsHeaders(creds: AwsCreds): Record<string, string> {
  return {
    'X-AWS-Access-Key-Id': creds.accessKeyId,
    'X-AWS-Secret-Access-Key': creds.secretAccessKey,
    'X-AWS-Session-Token': creds.sessionToken,
  };
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error?.message || `API error: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** GET /fm-bundles/by-project/:projectName -> list of bundles for a project. */
export async function getFmBundlesByProject(projectName: string): Promise<FmBundle[]> {
  const res = await apiFetch(`/fm-bundles/by-project/${encodeURIComponent(projectName)}`);
  const json = await parseJson<{ data: FmBundle[] }>(res);
  return json.data ?? [];
}

/**
 * GET /fm-bundles/:id -> a single bundle with its relations populated.
 * Uses Strapi 5 indexed populate notation (comma-separated populate strings are rejected with a
 * 400 ValidationError).
 */
export async function getFmBundle(id: string): Promise<FmBundle> {
  const relations = ['environment', 'fm_profiles', 'fm_provisioning_requests', 'project'];
  const populate = relations.map((r, i) => `populate[${i}]=${encodeURIComponent(r)}`).join('&');
  const res = await apiFetch(`/fm-bundles/${encodeURIComponent(id)}?${populate}`);
  const json = await parseJson<{ data: FmBundle }>(res);
  return json.data;
}

/**
 * GET /fm-bundles/:id/status -> live provisioning status.
 * Requires Image Builder account creds; throws MissingCredsError if absent.
 */
export async function getFmBundleStatus(id: string): Promise<FmBundleStatusResult> {
  const { creds } = await resolveAccountCreds(FM_PROVISIONING_ACCOUNT_ID);
  const res = await apiFetch(`/fm-bundles/${encodeURIComponent(id)}/status`, {
    headers: awsHeaders(creds),
  });
  return parseJson<FmBundleStatusResult>(res);
}

/** GET /fm-models/catalog?selectableOnly=true -> selectable model catalog. */
export async function getFmModelCatalog(selectableOnly = true): Promise<FmCatalog> {
  const res = await apiFetch(`/fm-models/catalog?selectableOnly=${selectableOnly ? 'true' : 'false'}`);
  return parseJson<FmCatalog>(res);
}

/**
 * POST /fm-models/sync -> refresh the model catalog from Bedrock.
 * Requires Application-account creds; throws MissingCredsError if absent.
 */
export async function syncFmModelCatalog(): Promise<FmModelSyncResult> {
  const { creds } = await resolveAccountCreds(FM_CATALOG_SYNC_ACCOUNT_ID);
  const res = await apiFetch(`/fm-models/sync`, {
    method: 'POST',
    headers: awsHeaders(creds),
  });
  return parseJson<FmModelSyncResult>(res);
}

/**
 * Derives a URL/resource-safe slug from an arbitrary string, matching the server's slug rule:
 * lowercase, trim, replace runs of non-[a-z0-9] with '-', strip leading/trailing '-'.
 */
export function deriveSlug(s: string): string {
  return (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Body accepted by POST /fm-bundles/create. Exactly one budget key and exactly one env key. */
export interface CreateFmBundleBody {
  projectId: string;
  team: string;
  models: Array<{ shortName: string; modelId: string; provider?: string }>;
  computeRoles: string[];
  serviceName?: string;
  businessEmails?: string[];
  monthlyBudgetUSD?: number;
  yearlyBudgetUSD?: number;
  environmentId?: string;
  newEnvironment?: { name: string; isProduction?: boolean };
}

export interface CreateFmBundleResult {
  bundle: FmBundle;
  request?: FmProvisioningRequest;
  dryRun?: boolean;
}

/**
 * POST /fm-bundles/create -> creates the bundle and triggers provisioning.
 * Requires Image Builder account creds; throws MissingCredsError if absent. On a non-OK
 * response, throws an Error carrying the server's error.message.
 */
export async function createFmBundle(body: CreateFmBundleBody): Promise<CreateFmBundleResult> {
  const { creds } = await resolveAccountCreds(FM_PROVISIONING_ACCOUNT_ID);
  const res = await apiFetch(`/fm-bundles/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...awsHeaders(creds) },
    body: JSON.stringify(body),
  });
  return parseJson<CreateFmBundleResult>(res);
}

/** Maps an FM bundle lifecycle status to a Cloudscape StatusIndicator type. */
export function fmStatusType(
  status: FmBundleStatus | string,
): 'success' | 'error' | 'in-progress' | 'stopped' | 'info' {
  switch (status) {
    case 'active':
      return 'success';
    case 'failed':
      return 'error';
    case 'provisioning':
    case 'deleting':
      return 'in-progress';
    case 'deleted':
      return 'stopped';
    default:
      return 'info';
  }
}
